import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BsPlusLg, BsTrash, BsXLg } from "react-icons/bs";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { useDirty } from "../unsaved";

// Booking Flow v2 policy (Settings > Booking Policy). One GET for the whole page, and one PATCH per
// card that sends only that card's section, so saving one card never re-submits another. The server
// (common-core BookingPolicy.validate) is the authority on every rule; the checks here only catch
// obvious typos before the round trip.
//
// Each booking keeps a copy of these values from the moment it is created, so a change applies to
// new bookings only.

const VIEW_URL = "/admin/business/site/booking-policy/view";
const UPDATE_URL = "/admin/business/site/booking-policy/update";

const CATEGORY_LABELS = {
    rental: "Rental",
    deliveryFee: "Delivery fee",
    pickupFee: "Pickup fee",
    protectionFee: "Trip protection",
    platformFee: "Platform fee",
    convenienceFee: "Convenience fee",
};

const str = (value) => (value === undefined || value === null ? "" : String(value));
const num = (value) => (value === "" || value === null || value === undefined ? NaN : Number(value));
const isWhole = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
const hoursText = (hours) => {
    const value = Number(hours);
    if (value >= 24 && value % 24 === 0) return `${value / 24} day${value === 24 ? "" : "s"}`;
    return `${value} hour${value === 1 ? "" : "s"}`;
};

// Shared save: PATCH one section, toast the result, reload the page data on success.
const useSaver = (reload) => {
    const [saving, setSaving] = useState(false);
    const save = async (data, successText) => {
        setSaving(true);
        const response = await Handler({ method: "patch", url: UPDATE_URL, data });
        if (response?.success) {
            toast.success(response?.data?.message === "Already upto date" ? "No changes to save" : successText);
            await reload();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save"));
        }
        setSaving(false);
    };
    return [saving, save];
};

const Card = ({ title, intro, children, onSave, onDefaults, saving }) => (
    <div className="settings-card mb-4">
        <h5 className="fw-semibold mb-2">{title}</h5>
        {intro && <p className="text-muted small mb-4">{intro}</p>}
        {children}
        <div className="settings-card-actions gap-2">
            {onDefaults && (
                <button type="button" className="btn btn-outline-secondary" onClick={onDefaults} disabled={saving}>
                    Use defaults
                </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                {saving ? "Saving..." : "Save changes"}
            </button>
        </div>
    </div>
);

const NumberInput = ({ id, label, hint, value, onChange, min, max, step = 1, suffix }) => (
    <div>
        <label className="form-label" htmlFor={id}>{label}</label>
        <div className="input-group">
            <input id={id} type="number" className="form-control" min={min} max={max} step={step} value={value} onChange={(e) => onChange(e.target.value)} />
            {suffix && <span className="input-group-text">{suffix}</span>}
        </div>
        {hint && <div className="form-text">{hint}</div>}
    </div>
);

// ------------------------------ Payment expiry ------------------------------
const PaymentExpiryCard = ({ policy, defaults, limits, reload }) => {
    const [value, setValue] = useState(str(policy.paymentExpiryMinutes));
    const [saving, save] = useSaver(reload);
    const { min, max } = limits.paymentExpiryMinutes;
    const onSave = () => {
        const minutes = num(value);
        if (!isWhole(minutes, min, max)) return toast.error(`Payment expiry must be a whole number from ${min} to ${max} minutes`);
        save({ paymentExpiryMinutes: minutes }, "Payment expiry updated");
    };
    useDirty("policy-expiry", value, str(policy.paymentExpiryMinutes), onSave, () => setValue(str(policy.paymentExpiryMinutes)));
    return (
        <Card
            title="Payment Expiry"
            intro="A booking that isn't paid within this time is cancelled and the car is released. Razorpay payment links can't expire sooner than 15 minutes, so a link paid after this time is flagged as a late payment and refunded once an admin approves."
            onSave={onSave}
            onDefaults={() => setValue(str(defaults.paymentExpiryMinutes))}
            saving={saving}
        >
            <div className="row">
                <div className="col-12 col-md-4">
                    <NumberInput id="policy-expiry" label="Expires after" value={value} onChange={setValue} min={min} max={max} suffix="minutes" />
                </div>
            </div>
        </Card>
    );
};

// ------------------------------ Cancellation bands ------------------------------
const bandRows = (bands) => bands.map((band) => ({
    hours: str(band.minHoursBeforeStart),
    refund: Object.fromEntries(Object.keys(CATEGORY_LABELS).map((key) => [key, str(band.refund?.[key])])),
}));

// "24 hours or more before start", "12 to 24 hours before start", "Under 5 hours, or after the start time".
const bandLabel = (rows, index) => {
    const sorted = rows.map((row) => num(row.hours)).filter((hours) => Number.isFinite(hours)).sort((a, b) => b - a);
    const hours = num(rows[index].hours);
    if (!Number.isFinite(hours)) return "Set the hours";
    const above = sorted.filter((value) => value > hours).pop();
    if (hours === 0) return above === undefined ? "Any time, including after the start" : `Under ${hoursText(above)} before start, or after the start time`;
    if (above === undefined) return `${hoursText(hours)} or more before start`;
    return `${hoursText(hours)} to ${hoursText(above)} before start`;
};

const CancellationCard = ({ policy, defaults, limits, categories, reload }) => {
    const [rows, setRows] = useState(bandRows(policy.cancellationBands));
    const [saving, save] = useSaver(reload);

    const setHours = (index, value) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, hours: value } : row)));
    const setPct = (index, key, value) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, refund: { ...row.refund, [key]: value } } : row)));
    const addBand = () => setRows((prev) => [...prev, { hours: "", refund: Object.fromEntries(categories.map((key) => [key, "100"])) }]);
    const removeBand = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

    const onSave = () => {
        const { bands: bandLimits, bandHours, percent } = limits;
        if (rows.length < bandLimits.min || rows.length > bandLimits.max) return toast.error(`Add between ${bandLimits.min} and ${bandLimits.max} bands`);
        const hoursList = rows.map((row) => num(row.hours));
        if (hoursList.some((hours) => !Number.isFinite(hours) || hours < bandHours.min || hours > bandHours.max)) return toast.error(`Band hours must be from ${bandHours.min} to ${bandHours.max}`);
        if (new Set(hoursList).size !== hoursList.length) return toast.error("Two bands start at the same hour");
        if (!hoursList.includes(0)) return toast.error("Add a band starting at 0 hours. It covers the last hours before the start and after the start time");
        for (const row of rows) {
            for (const key of categories) {
                if (!isWhole(num(row.refund[key]), percent.min, percent.max)) return toast.error(`${CATEGORY_LABELS[key] || key}: refund must be a whole percentage from 0 to 100`);
            }
        }
        const bands = rows
            .map((row) => ({ minHoursBeforeStart: num(row.hours), refund: Object.fromEntries(categories.map((key) => [key, num(row.refund[key])])) }))
            .sort((a, b) => b.minHoursBeforeStart - a.minHoursBeforeStart);
        save({ cancellationBands: bands }, "Cancellation refunds updated");
    };

    useDirty("policy-cancellation", rows, bandRows(policy.cancellationBands), onSave, () => setRows(bandRows(policy.cancellationBands)));
    return (
        <Card
            title="Cancellation Refunds"
            intro="How much of each payment is refunded when a booked trip is cancelled, by how long before the start it is cancelled. The security deposit is always refunded in full."
            onSave={onSave}
            onDefaults={() => setRows(bandRows(defaults.cancellationBands))}
            saving={saving}
        >
            <div className="table-responsive">
                <table className="table align-middle mb-2">
                    <thead>
                        <tr>
                            <th style={{ minWidth: 190 }}>Cancelled</th>
                            <th style={{ minWidth: 110 }}>From (hours before start)</th>
                            {categories.map((key) => <th key={key} className="text-center" style={{ minWidth: 96 }}>{CATEGORY_LABELS[key] || key} %</th>)}
                            <th className="text-center" style={{ minWidth: 96 }}>Security deposit %</th>
                            <th aria-label="Remove" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                <td className="small">{bandLabel(rows, index)}</td>
                                <td>
                                    <input
                                        type="number" min="0" step="0.5" className="form-control form-control-sm"
                                        aria-label={`Band ${index + 1} hours before start`}
                                        value={row.hours} onChange={(e) => setHours(index, e.target.value)}
                                    />
                                </td>
                                {categories.map((key) => (
                                    <td key={key}>
                                        <input
                                            type="number" min="0" max="100" step="1" className="form-control form-control-sm text-center"
                                            aria-label={`Band ${index + 1} ${CATEGORY_LABELS[key] || key} refund percent`}
                                            value={row.refund[key]} onChange={(e) => setPct(index, key, e.target.value)}
                                        />
                                    </td>
                                ))}
                                <td><input type="text" className="form-control form-control-sm text-center" value="100" disabled aria-label="Security deposit is always refunded in full" /></td>
                                <td>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBand(index)} disabled={rows.length <= 1} aria-label={`Remove band ${index + 1}`}>
                                        <BsTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={addBand} disabled={rows.length >= limits.bands.max}>
                <BsPlusLg className="me-1" /> Add band
            </button>
            <div className="form-text mt-2">A band starting at 0 hours is required. It applies in the last hours before the start and after the start time.</div>
        </Card>
    );
};

// ------------------------------ Trip start and end ------------------------------
const TripCard = ({ policy, defaults, limits, reload }) => {
    const fromPolicy = (p) => ({
        startWindowMinutes: str(p.startWindowMinutes),
        excessHourGraceMinutes: str(p.excessHourGraceMinutes),
        startMin: str(p.photos.start.min), startMax: str(p.photos.start.max),
        endMin: str(p.photos.end.min), endMax: str(p.photos.end.max),
    });
    const [form, setForm] = useState(fromPolicy(policy));
    const [saving, save] = useSaver(reload);
    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const onSave = () => {
        const startWindow = num(form.startWindowMinutes);
        const grace = num(form.excessHourGraceMinutes);
        if (!isWhole(startWindow, limits.startWindowMinutes.min, limits.startWindowMinutes.max)) return toast.error(`Start window must be ${limits.startWindowMinutes.min}–${limits.startWindowMinutes.max} minutes`);
        if (!isWhole(grace, limits.excessHourGraceMinutes.min, limits.excessHourGraceMinutes.max)) return toast.error(`Late-return grace must be ${limits.excessHourGraceMinutes.min}–${limits.excessHourGraceMinutes.max} minutes`);
        const photos = {
            start: { min: num(form.startMin), max: num(form.startMax) },
            end: { min: num(form.endMin), max: num(form.endMax) },
        };
        for (const [stage, label] of [["start", "Start"], ["end", "End"]]) {
            const { min, max } = photos[stage];
            if (!isWhole(min, limits.photos.min, limits.photos.max) || !isWhole(max, limits.photos.min, limits.photos.max)) return toast.error(`${label} photos must be ${limits.photos.min}–${limits.photos.max}`);
            if (min > max) return toast.error(`${label} photos: the minimum can't be more than the maximum`);
        }
        save({ startWindowMinutes: startWindow, excessHourGraceMinutes: grace, photos }, "Trip settings updated");
    };

    useDirty("policy-trip", form, fromPolicy(policy), onSave, () => setForm(fromPolicy(policy)));
    return (
        <Card
            title="Trip Start and End"
            intro="When the start photos and readings open, how many car photos are needed at the start and end, and how late a return can be before extra hours are charged."
            onSave={onSave}
            onDefaults={() => setForm(fromPolicy(defaults))}
            saving={saving}
        >
            <div className="row g-4">
                <div className="col-12 col-md-6">
                    <NumberInput id="policy-window" label="Start opens before start time" value={form.startWindowMinutes} onChange={set("startWindowMinutes")} min={0} suffix="minutes"
                        hint="Step 1 (photos) and Step 2 (readings) unlock this long before the start. An admin can still start early, with a reason." />
                </div>
                <div className="col-12 col-md-6">
                    <NumberInput id="policy-grace" label="Late-return grace" value={form.excessHourGraceMinutes} onChange={set("excessHourGraceMinutes")} min={0} suffix="minutes"
                        hint="A return within this time after the end is free. After it, every started hour is charged." />
                </div>
                <div className="col-6 col-md-3"><NumberInput id="policy-start-min" label="Start photos, minimum" value={form.startMin} onChange={set("startMin")} min={1} /></div>
                <div className="col-6 col-md-3"><NumberInput id="policy-start-max" label="Start photos, maximum" value={form.startMax} onChange={set("startMax")} min={1} /></div>
                <div className="col-6 col-md-3"><NumberInput id="policy-end-min" label="End photos, minimum" value={form.endMin} onChange={set("endMin")} min={1} /></div>
                <div className="col-6 col-md-3"><NumberInput id="policy-end-max" label="End photos, maximum" value={form.endMax} onChange={set("endMax")} min={1} /></div>
            </div>
        </Card>
    );
};

// ------------------------------ Gaps between bookings ------------------------------
// One value per panel: the gap used is the one for whoever does the action (the customer in the
// app, or an admin / staff member doing it for them). Customers can't change the car.
const GAP_PANELS = [
    { key: "admin", label: "Admin" },
    { key: "staff", label: "Staff" },
    { key: "customer", label: "Customer" },
];
const GAP_FIELDS = [
    { key: "extension", label: "Before extending a trip", hint: "The car's next booking must start at least this long after the extended end.", panels: ["admin", "staff", "customer"] },
    { key: "newBooking", label: "Between new bookings", hint: "Kept free before and after an existing booking when a new one is made. 0 = only overlaps are refused.", panels: ["admin", "staff", "customer"] },
    { key: "carChange", label: "When changing the car", hint: "The replacement car must be free for this long around the trip. Customers can't change the car.", panels: ["admin", "staff"] },
];

const GapsCard = ({ policy, defaults, limits, reload }) => {
    const fromPolicy = (p) => Object.fromEntries(GAP_FIELDS.map((field) => [
        field.key,
        Object.fromEntries(field.panels.map((panel) => [panel, str(p.gapHours?.[field.key]?.[panel])])),
    ]));
    const [form, setForm] = useState(fromPolicy(policy));
    const [saving, save] = useSaver(reload);
    const setCell = (type, panel, value) => setForm((prev) => ({ ...prev, [type]: { ...prev[type], [panel]: value } }));

    const onSave = () => {
        const gapHours = {};
        for (const field of GAP_FIELDS) {
            gapHours[field.key] = {};
            for (const panel of field.panels) {
                const value = num(form[field.key][panel]);
                if (!Number.isFinite(value) || value < limits.gapHours.min || value > limits.gapHours.max) {
                    const panelLabel = GAP_PANELS.find((item) => item.key === panel)?.label;
                    return toast.error(`${field.label} (${panelLabel}): gap must be ${limits.gapHours.min}–${limits.gapHours.max} hours`);
                }
                gapHours[field.key][panel] = value;
            }
        }
        save({ gapHours }, "Gaps updated");
    };

    useDirty("policy-gaps", form, fromPolicy(policy), onSave, () => setForm(fromPolicy(policy)));
    return (
        <Card
            title="Gaps Between Bookings"
            intro="Free time kept on a car between one booking and the next, in hours. Each panel has its own value: the one used is for whoever does the action."
            onSave={onSave}
            onDefaults={() => setForm(fromPolicy(defaults))}
            saving={saving}
        >
            <div className="table-responsive">
                <table className="table align-middle mb-0">
                    <thead>
                        <tr>
                            <th style={{ minWidth: 220 }}>Gap</th>
                            {GAP_PANELS.map((panel) => <th key={panel.key} className="text-center" style={{ minWidth: 110 }}>{panel.label} (hours)</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {GAP_FIELDS.map((field) => (
                            <tr key={field.key}>
                                <td>
                                    <div className="fw-semibold">{field.label}</div>
                                    <div className="text-muted small">{field.hint}</div>
                                </td>
                                {GAP_PANELS.map((panel) => (
                                    <td key={panel.key}>
                                        {field.panels.includes(panel.key) ? (
                                            <input
                                                type="number" min="0" max={limits.gapHours.max} step="0.5"
                                                className="form-control form-control-sm text-center"
                                                aria-label={`${field.label}, ${panel.label}, hours`}
                                                value={form[field.key][panel.key]}
                                                onChange={(e) => setCell(field.key, panel.key, e.target.value)}
                                            />
                                        ) : (
                                            <div className="text-center text-muted small" title="Customers can't change the car">Not used</div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

// ------------------------------ Customer limits ------------------------------
// About the customer, not one booking: these apply to every booking made or started from now on, not just new ones.
const CustomerLimitsCard = ({ policy, defaults, limits, reload }) => {
    const [live, setLive] = useState(str(policy.limits.maxLiveBookings));
    const [gap, setGap] = useState(str(policy.limits.customerGapHours));
    const [saving, save] = useSaver(reload);
    const onSave = () => {
        const count = num(live);
        const hours = num(gap);
        if (!isWhole(count, limits.maxLiveBookings.min, limits.maxLiveBookings.max)) return toast.error(`Live bookings must be ${limits.maxLiveBookings.min}–${limits.maxLiveBookings.max}`);
        if (!Number.isFinite(hours) || hours < limits.customerGapHours.min || hours > limits.customerGapHours.max) return toast.error(`Gap must be ${limits.customerGapHours.min}–${limits.customerGapHours.max} hours`);
        save({ limits: { maxLiveBookings: count, customerGapHours: hours } }, "Customer limits updated");
    };
    const saved = { live: str(policy.limits.maxLiveBookings), gap: str(policy.limits.customerGapHours) };
    useDirty("policy-limits", { live, gap }, saved, onSave, () => { setLive(saved.live); setGap(saved.gap); });
    return (
        <Card
            title="Customer limits"
            intro="Live means Initiated, Booked, Started or Ended. A customer with an unpaid balance can't book or start anything. Unlike the other cards, these apply at once to every customer, not only to new bookings."
            onSave={onSave}
            onDefaults={() => { setLive(str(defaults.limits.maxLiveBookings)); setGap(str(defaults.limits.customerGapHours)); }}
            saving={saving}
        >
            <div className="row g-4">
                <div className="col-12 col-md-4">
                    <NumberInput id="policy-live" label="Live bookings per customer" value={live} onChange={setLive} min={1} suffix="bookings" hint="A new booking is refused until one is cancelled or completed. Admin can override with a reason." />
                </div>
                <div className="col-12 col-md-4">
                    <NumberInput id="policy-cgap" label="Gap between a customer's bookings" value={gap} onChange={setGap} min={0} step="0.5" suffix="hours" hint="Kept free between one customer's own bookings. Also used when they extend." />
                </div>
            </div>
        </Card>
    );
};

// ------------------------------ Extensions ------------------------------
const ExtensionCard = ({ policy, defaults, limits, reload }) => {
    const [maxHours, setMaxHours] = useState(str(policy.extension.maxHours));
    const [maxCount, setMaxCount] = useState(str(policy.extension.maxCount));
    const [chargeProtection, setChargeProtection] = useState(!!policy.extension.chargeProtection);
    const [saving, save] = useSaver(reload);
    const onSave = () => {
        const value = num(maxHours);
        const count = num(maxCount);
        if (!isWhole(value, limits.extensionMaxHours.min, limits.extensionMaxHours.max)) return toast.error(`Maximum extension must be ${limits.extensionMaxHours.min}–${limits.extensionMaxHours.max} hours`);
        if (!isWhole(count, limits.extensionMaxCount.min, limits.extensionMaxCount.max)) return toast.error(`Number of extensions must be ${limits.extensionMaxCount.min}–${limits.extensionMaxCount.max}`);
        save({ extension: { maxHours: value, maxCount: count, chargeProtection } }, "Extension settings updated");
    };
    const savedExt = { maxHours: str(policy.extension.maxHours), maxCount: str(policy.extension.maxCount), chargeProtection: !!policy.extension.chargeProtection };
    useDirty("policy-extension", { maxHours, maxCount, chargeProtection }, savedExt, onSave, () => { setMaxHours(savedExt.maxHours); setMaxCount(savedExt.maxCount); setChargeProtection(savedExt.chargeProtection); });
    return (
        <Card
            title="Extensions"
            onSave={onSave}
            onDefaults={() => { setMaxHours(str(defaults.extension.maxHours)); setMaxCount(str(defaults.extension.maxCount)); setChargeProtection(!!defaults.extension.chargeProtection); }}
            saving={saving}
        >
            <div className="row g-4 align-items-start">
                <div className="col-12 col-md-4">
                    <NumberInput id="policy-ext-max" label="Maximum extension per trip" value={maxHours} onChange={setMaxHours} min={1} suffix="hours" hint="Total of all extensions on one trip." />
                </div>
                <div className="col-12 col-md-4">
                    <NumberInput id="policy-ext-count" label="Extensions per trip" value={maxCount} onChange={setMaxCount} min={1} suffix="times" hint="How many separate times one trip can be extended." />
                </div>
                <div className="col-12">
                    <div className="settings-toggle-row">
                        <div>
                            <div className="fw-semibold">Charge trip protection on extensions</div>
                            <div className="text-muted small">When on, the protection top-up for the extra hours is added to the extension payment and shown as its own line.</div>
                        </div>
                        <div className="form-check form-switch fs-4 m-0">
                            <input className="form-check-input" type="checkbox" role="switch" aria-label="Charge trip protection on extensions" checked={chargeProtection} onChange={(e) => setChargeProtection(e.target.checked)} />
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

// ------------------------------ Pending reminders ------------------------------
const RemindersCard = ({ policy, defaults, limits, reload }) => {
    const [list, setList] = useState(policy.pendingReminderHours || []);
    const [draft, setDraft] = useState("");
    const [saving, save] = useSaver(reload);

    const add = () => {
        const value = num(draft);
        if (!isWhole(value, limits.reminderHours.min, limits.reminderHours.max)) return toast.error(`A reminder must be ${limits.reminderHours.min}–${limits.reminderHours.max} hours after Pending`);
        if (list.includes(value)) return toast.error("There's already a reminder at that time");
        if (list.length >= limits.reminders.max) return toast.error(`Up to ${limits.reminders.max} reminders`);
        setList((prev) => [...prev, value].sort((a, b) => a - b));
        setDraft("");
    };

    const onSave = () => save({ pendingReminderHours: list }, "Reminders updated");
    useDirty("policy-reminders", list, policy.pendingReminderHours || [], onSave, () => setList(policy.pendingReminderHours || []));
    return (
        <Card
            title="Pending Balance Reminders"
            intro="When fines are more than the deposit, the booking goes to Pending and the customer gets a payment link. These reminders follow, counted from when it went Pending, until the balance is paid."
            onSave={onSave}
            onDefaults={() => setList([...defaults.pendingReminderHours])}
            saving={saving}
        >
            <div className="d-flex flex-wrap gap-2 mb-3">
                {list.length === 0 && <span className="text-muted small">No reminders. Only the first message is sent.</span>}
                {list.map((hours) => (
                    <span key={hours} className="badge rounded-pill text-bg-light border d-inline-flex align-items-center gap-2 px-3 py-2 fw-normal">
                        After {hoursText(hours)}
                        <button type="button" className="btn btn-link p-0 text-danger lh-1" onClick={() => setList((prev) => prev.filter((value) => value !== hours))} aria-label={`Remove the reminder after ${hoursText(hours)}`}>
                            <BsXLg />
                        </button>
                    </span>
                ))}
            </div>
            <div className="row g-2 align-items-end">
                <div className="col-8 col-md-4">
                    <NumberInput id="policy-reminder-new" label="Add a reminder after" value={draft} onChange={setDraft} min={1} suffix="hours" />
                </div>
                <div className="col-4 col-md-2">
                    <button type="button" className="btn btn-outline-primary w-100" onClick={add}><BsPlusLg className="me-1" /> Add</button>
                </div>
            </div>
        </Card>
    );
};

// ------------------------------ Page ------------------------------
const BookingPolicyTab = () => {
    const [state, setState] = useState(null);
    const [failed, setFailed] = useState(false);
    // Bumped after every successful save so each card starts again from the saved values.
    const [version, setVersion] = useState(0);

    const load = useCallback(async () => {
        const response = await Handler({ method: "GET", url: VIEW_URL });
        if (response?.success && response?.data?.data?.policy) {
            setState(response.data.data);
            setFailed(false);
            setVersion((value) => value + 1);
        } else {
            setFailed(true);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (failed && !state) {
        return (
            <div className="settings-card">
                <p className="mb-3">Couldn't load the booking policy.</p>
                <button type="button" className="btn btn-outline-primary" onClick={load}>Try again</button>
            </div>
        );
    }
    if (!state) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={120} className="mb-3" />
            </div>
        );
    }

    const props = { policy: state.policy, defaults: state.defaults, limits: state.limits, categories: state.refundCategories, reload: load };
    return (
        <>
            <div className="alert alert-info small mb-4">
                Every booking keeps a copy of these settings from the moment it's created. A change here applies to new bookings only.
            </div>
            <PaymentExpiryCard key={`expiry-${version}`} {...props} />
            <CancellationCard key={`cancel-${version}`} {...props} />
            <TripCard key={`trip-${version}`} {...props} />
            <GapsCard key={`gaps-${version}`} {...props} />
            <CustomerLimitsCard key={`limits-${version}`} {...props} />
            <ExtensionCard key={`ext-${version}`} {...props} />
            <RemindersCard key={`rem-${version}`} {...props} />
        </>
    );
};

export default BookingPolicyTab;
